import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '@app/store/store'
import {
  getAllFirmsTable,
  createFirm,
  deleteFirm,
  PartOfDataAllFirmsResponse,
  FirmsResponse,
  getFirmDetails,
  FirmDetailsResponse,
  getDevicesForFirms,
  FirmDevicesResponse,
  getPartOfDataAllFirms,
  updateFirm,
  UpdateFirmPayload,
  CreateFirmPayload,
  FirmCreateData,
} from '@app/api/firms.api'

import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

import { getProfileData } from './profile-slice'
import { GetStateFn } from './types'

type FirmsState = {
  allFirmsData: Record<string, FirmsResponse>
  allFirmsStatus: Status
  allFirmsPartStatus: Status
  firmDetailsStatusById: Record<string, Status>
  firmAssignmentsStatus: Status
  createFirmStatus: Status
  updateFirmStatus: Status
  deleteFirmStatus: Status
}

const initialState: FirmsState = {
  allFirmsData: {},
  allFirmsStatus: 'idle',
  allFirmsPartStatus: 'idle',
  firmDetailsStatusById: {},
  firmAssignmentsStatus: 'idle',
  createFirmStatus: 'idle',
  updateFirmStatus: 'idle',
  deleteFirmStatus: 'idle',
}

export const getAllFirmsTableData = createAsyncThunk<FirmsResponse[]>(
  'firms/allTableData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllFirmsTable()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getFirmDetailsData = createAsyncThunk<FirmDetailsResponse, string>(
  'firms/details',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getFirmDetails(id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getDevicesForFirmsData = createAsyncThunk<FirmDevicesResponse[], string[]>(
  'firms/getDevicesForFirms',
  async (firmIds, { rejectWithValue }) => {
    try {
      const res = await getDevicesForFirms(firmIds)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllFirmsPartOfData = createAsyncThunk<PartOfDataAllFirmsResponse[]>(
  'firms/getAllFirmsPartOfData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getPartOfDataAllFirms()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createFirmData = createAsyncThunk<FirmCreateData, CreateFirmPayload>(
  'firms/createFirm',
  async (createFirmPayload, { rejectWithValue }) => {
    try {
      const res = await createFirm(createFirmPayload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateFirmData = createAsyncThunk<void, UpdateFirmPayload>(
  'firms/updateFirm',
  async (updateFirmPayload, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await updateFirm(updateFirmPayload)

      refreshProfileIfFirmAssigned(getState, dispatch, updateFirmPayload.id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteFirmData = createAsyncThunk<string, string>(
  'firms/deleteFirm',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await deleteFirm(id)

      refreshProfileIfFirmAssigned(getState, dispatch, id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const firmsSlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllFirmsTableData.pending, (state) => {
        state.allFirmsStatus = 'loading'
      })
      .addCase(getFirmDetailsData.pending, (state, action) => {
        state.firmDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getAllFirmsPartOfData.pending, (state) => {
        state.allFirmsPartStatus = 'loading'
      })
      .addCase(getDevicesForFirmsData.pending, (state) => {
        state.firmAssignmentsStatus = 'loading'
      })
      .addCase(createFirmData.pending, (state) => {
        state.createFirmStatus = 'loading'
      })
      .addCase(updateFirmData.pending, (state) => {
        state.updateFirmStatus = 'loading'
      })
      .addCase(deleteFirmData.pending, (state) => {
        state.deleteFirmStatus = 'loading'
      })
      .addCase(getAllFirmsTableData.rejected, (state) => {
        state.allFirmsStatus = 'failed'
      })
      .addCase(getFirmDetailsData.rejected, (state, action) => {
        state.firmDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getAllFirmsPartOfData.rejected, (state) => {
        state.allFirmsPartStatus = 'failed'
      })
      .addCase(getDevicesForFirmsData.rejected, (state) => {
        state.firmAssignmentsStatus = 'failed'
      })
      .addCase(createFirmData.rejected, (state) => {
        state.createFirmStatus = 'failed'
      })
      .addCase(updateFirmData.rejected, (state) => {
        state.updateFirmStatus = 'failed'
      })
      .addCase(deleteFirmData.rejected, (state) => {
        state.deleteFirmStatus = 'failed'
      })
      .addCase(getAllFirmsTableData.fulfilled, (state, action) => {
        action.payload.forEach((firm) => (state.allFirmsData[firm.id] = { ...state.allFirmsData[firm.id], ...firm }))

        state.allFirmsStatus = 'succeeded'
        if (state.firmAssignmentsStatus !== 'loading') state.firmAssignmentsStatus = 'idle'
      })
      .addCase(getDevicesForFirmsData.fulfilled, (state, action) => {
        action.payload.forEach(({ id, devices }) => {
          if (state.allFirmsData[id]) state.allFirmsData[id].devices = devices
        })

        state.firmAssignmentsStatus = Object.values(state.allFirmsData).some((firm) => !firm.devices)
          ? 'idle'
          : 'succeeded'
      })

      .addCase(getFirmDetailsData.fulfilled, (state, action) => {
        const firmId = action.meta.arg
        const { addresses, devices } = action.payload
        const existingFirm = state.allFirmsData[firmId]
        const address = { ...state.allFirmsData[firmId]?.addresses?.[0], ...addresses?.[0] }

        state.allFirmsData[firmId] = {
          ...existingFirm,
          addresses: [address],
          devices: devices,
        }

        state.firmDetailsStatusById[firmId] = 'succeeded'
      })
      .addCase(getAllFirmsPartOfData.fulfilled, (state, action) => {
        action.payload.forEach((firm) => {
          const id = firm.id
          const existing = state.allFirmsData[id] ?? {}

          state.allFirmsData[id] = {
            ...existing,
            ...firm,
          }
        })

        state.allFirmsPartStatus = 'succeeded'
      })
      .addCase(updateFirmData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        const existing = state.allFirmsData[id] ?? {}
        const { usersToUpdate, ...restData } = data

        state.allFirmsData[id] = {
          ...existing,
          ...restData,
        }

        state.updateFirmStatus = 'succeeded'
      })
      .addCase(createFirmData.fulfilled, (state, action) => {
        const data = action.meta.arg
        const { id, addresses, phones } = action.payload
        const newAddress = {
          ...data.addresses[0],
          id: addresses[0].id,
        }
        const newPhone = {
          ...data.phones[0],
          id: phones[0].id,
        }
        const newFirm = {
          ...data,
          id,
          addresses: [newAddress],
          phones: [newPhone],
        }

        state.allFirmsData = {
          [id]: newFirm,
          ...state.allFirmsData,
        }

        state.createFirmStatus = 'succeeded'
      })
      .addCase(deleteFirmData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.allFirmsData[id]

        state.deleteFirmStatus = 'succeeded'
      })
  },
})

export default firmsSlice.reducer

const getProfileIdIfFirmAssigned = (state: RootState, firmId: string): string | null => {
  const profile = state.profile?.data
  if (!profile?.firms?.length) return null

  const firmFound = profile.firms.some(({ id }) => id === firmId)
  return firmFound ? profile.id : null
}

const refreshProfileIfFirmAssigned = async (getState: GetStateFn, dispatch: (action: any) => any, firmId: string) => {
  const profileId = getProfileIdIfFirmAssigned(getState() as RootState, firmId)

  if (profileId) await dispatch(getProfileData(profileId))
}
